import { Dispatch, useEffect, useRef } from "react";
import { CraneShift, generatedApi } from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { useAppDispatch } from "src/store/store";
import { shiftsActions, SHIFTS_PER_FETCH, ShiftsAction } from "./types";

interface UseShiftBufferParams {
  shifts: CraneShift[];
  selectedIndex: number;
  bufferSize: number;
  dispatch: Dispatch<ShiftsAction>;
}

interface FetchShiftsParams {
  tenantName: string;
  referenceTimestamp: string;
  before: number;
  after: number;
}

type FetchShiftsFn = (
  params: FetchShiftsParams
) => Promise<{ shifts: CraneShift[] }>;

interface FetchBufferParams {
  shifts: CraneShift[];
  selectedIndex: number;
  bufferSize: number;
  tenantName: string;
  fetchShifts: FetchShiftsFn;
  dispatch: Dispatch<ShiftsAction>;
}

const fetchEarlierShifts = async (
  shifts: CraneShift[],
  tenantName: string,
  fetchShifts: FetchShiftsFn,
  dispatch: Dispatch<ShiftsAction>
): Promise<void> => {
  const earliestShift = shifts[0];
  if (!earliestShift) return;

  const result = await fetchShifts({
    tenantName,
    referenceTimestamp: earliestShift.shift_start_time,
    before: SHIFTS_PER_FETCH,
    after: 0,
  });

  if (result.shifts.length > 0) {
    dispatch(shiftsActions.addShifts(result.shifts, false));
  }
};

const fetchLaterShifts = async (
  shifts: CraneShift[],
  tenantName: string,
  fetchShifts: FetchShiftsFn,
  dispatch: Dispatch<ShiftsAction>
): Promise<void> => {
  const latestShift = shifts[shifts.length - 1];
  if (!latestShift) return;

  const result = await fetchShifts({
    tenantName,
    referenceTimestamp: latestShift.shift_end_time,
    before: 0,
    after: SHIFTS_PER_FETCH,
  });

  if (result.shifts.length > 0) {
    dispatch(shiftsActions.addShifts(result.shifts, false));
  }
};

const fetchBufferShifts = async ({
  shifts,
  selectedIndex,
  bufferSize,
  tenantName,
  fetchShifts,
  dispatch,
}: FetchBufferParams): Promise<void> => {
  const needsEarlierShifts = selectedIndex < bufferSize;
  const distanceFromEnd = shifts.length - 1 - selectedIndex;
  const needsLaterShifts = distanceFromEnd < bufferSize;

  const fetchPromises: Promise<void>[] = [];

  if (needsEarlierShifts) {
    fetchPromises.push(
      fetchEarlierShifts(shifts, tenantName, fetchShifts, dispatch)
    );
  }

  if (needsLaterShifts) {
    fetchPromises.push(
      fetchLaterShifts(shifts, tenantName, fetchShifts, dispatch)
    );
  }

  await Promise.all(fetchPromises);
};

export const useShiftBuffer = ({
  shifts,
  selectedIndex,
  bufferSize,
  dispatch,
}: UseShiftBufferParams): void => {
  const tenantName = useTenant();
  const appDispatch = useAppDispatch();
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (bufferSize <= 0 || shifts.length === 0 || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    const fetchShifts: FetchShiftsFn = async (params) => {
      const subscription = appDispatch(
        generatedApi.endpoints.getStrategyDashboardShifts.initiate(params)
      );
      try {
        return await subscription.unwrap();
      } catch {
        return { shifts: [] };
      } finally {
        subscription.unsubscribe();
      }
    };

    void fetchBufferShifts({
      shifts,
      selectedIndex,
      bufferSize,
      tenantName,
      fetchShifts,
      dispatch,
    }).finally(() => {
      isFetchingRef.current = false;
    });
  }, [selectedIndex, shifts, bufferSize, appDispatch, tenantName, dispatch]);
};
